import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type Hls from 'hls.js';
import { getMatchStream, MatchStream } from 'services/api/matchDetailsService';
import styles from './MatchVideo.module.css';

/** After a stream drops, try again this often. */
const RETRY_MS = 5_000;

interface Props {
  fi: number | string;
  /** The stream the tab was shown for; the one played is asked anew. */
  stream: MatchStream;
}

/**
 * The video of a match in play: an HLS stream, muted at first (browsers only
 * start sound after a tap), with sound and full screen on the player's own
 * controls. The provider's links carry a token that runs out quickly, so the
 * link is asked anew when the tab is opened and again whenever the stream
 * drops. Safari plays HLS natively; elsewhere hls.js does, loaded only when
 * a video is opened.
 */
export const MatchVideo = ({ fi, stream }: Props) => {
  const { t } = useTranslation();
  const video = useRef<HTMLVideoElement>(null);
  const [state, setState] = useState<
    'loading' | 'playing' | 'retrying' | 'unavailable'
  >('loading');
  // n: a new link is played even when its address did not change.
  const [link, setLink] = useState<{ url: string; n: number } | null>(null);
  const mounted = useRef(true);
  const refreshTimer = useRef<number>();

  const refresh = useCallback(async () => {
    window.clearTimeout(refreshTimer.current);
    const fresh = stream.test ? stream : await getMatchStream(fi, true);
    if (!mounted.current) return;
    if (!fresh) {
      setState('unavailable');
      refreshTimer.current = window.setTimeout(refresh, RETRY_MS * 3);
      return;
    }
    setLink((l) => ({ url: fresh.url, n: (l?.n ?? 0) + 1 }));
  }, [fi, stream]);

  useEffect(() => {
    mounted.current = true;
    void refresh();
    return () => {
      mounted.current = false;
      window.clearTimeout(refreshTimer.current);
    };
  }, [refresh]);

  useEffect(() => {
    const el = video.current;
    if (!el || !link) return;
    const url = link.url;
    let hls: Hls | null = null;
    let retry: number | undefined;
    let alive = true;
    const playing = () => alive && setState('playing');
    el.addEventListener('playing', playing);

    // A dropped stream: wait a little, then play a new link.
    const again = () => {
      if (!alive) return;
      setState('retrying');
      retry = window.setTimeout(() => void refresh(), RETRY_MS);
    };

    const start = async () => {
      if (!alive) return;
      setState((s) =>
        s === 'playing' || s === 'retrying' ? 'retrying' : 'loading'
      );
      if (el.canPlayType('application/vnd.apple.mpegurl')) {
        el.src = url;
        el.play().catch(() => {});
        return;
      }
      const { default: HlsJs } = await import('hls.js');
      if (!alive) return;
      if (!HlsJs.isSupported()) {
        el.src = url;
        return;
      }
      hls?.destroy();
      hls = new HlsJs({ lowLatencyMode: true, backBufferLength: 30 });
      hls.on(HlsJs.Events.ERROR, (_e, data) => {
        if (!data.fatal || !alive) return;
        hls?.destroy();
        hls = null;
        again();
      });
      hls.loadSource(url);
      hls.attachMedia(el);
      hls.on(HlsJs.Events.MANIFEST_PARSED, () => {
        el.play().catch(() => {});
      });
    };
    const onNativeError = () => {
      if (!alive || hls) return;
      again();
    };
    el.addEventListener('error', onNativeError);
    void start();

    return () => {
      alive = false;
      window.clearTimeout(retry);
      el.removeEventListener('playing', playing);
      el.removeEventListener('error', onNativeError);
      hls?.destroy();
      el.removeAttribute('src');
      el.load();
    };
  }, [link, refresh]);

  return (
    <div className={styles.wrapper}>
      <div className={styles.frame}>
        <video
          ref={video}
          className={styles.video}
          controls
          muted
          playsInline
          autoPlay
        />
        {state !== 'playing' && (
          <div className={styles.overlay}>
            {state !== 'unavailable' && (
              <span className={styles.spinner} aria-hidden="true" />
            )}
            {t(
              state === 'unavailable'
                ? 'video_unavailable'
                : state === 'retrying'
                  ? 'video_reconnecting'
                  : 'video_loading'
            )}
          </div>
        )}
        {stream.test && (
          <span className={styles.testBadge}>{t('video_test_badge')}</span>
        )}
      </div>
      <p className={styles.note}>
        {t(stream.test ? 'video_test_note' : 'video_note')}
      </p>
    </div>
  );
};
